import { Group } from '@/components/_common';
import StatusTag from '@/components/_common/StatusTag';
import { useMatrix } from '@/core/domain/enterprise/Matrix/Matrix.hooks';
import { usePurchaseSendBoschXml } from '@/core/domain/purchase/Purchase.hooks';
import {
  Purchase,
  PurchaseBoschXmlStatus,
  PurchaseBoschXmlStatusColor,
  PurchaseBoschXmlStatusLabel,
} from '@/core/domain/purchase/Purchase.types';
import { MatrixPartnerOrigin } from '@/types/Register';
import { Button, Col, Modal, Popover } from 'antd';
import moment from 'moment';
import { useCallback, useMemo } from 'react';

interface Props {
  purchase?: Purchase;
}

export default function PurchaseSendBoschXml({ purchase }: Props) {
  const sendBoschXml = usePurchaseSendBoschXml();

  const isDelivered = useMemo(
    () => purchase?.boschXmlSendStatus === PurchaseBoschXmlStatus.DELIVERED,
    [purchase?.boschXmlSendStatus],
  );

  const isRejected = useMemo(
    () => purchase?.boschXmlSendStatus === PurchaseBoschXmlStatus.REJECTED,
    [purchase?.boschXmlSendStatus],
  );

  const disabled = useMemo(
    () =>
      !(
        !!purchase?.issueDate &&
        moment(purchase.issueDate).add(48, 'hours').isSameOrBefore(moment())
      ),
    [purchase?.issueDate],
  );

  const matrixDetail = useMatrix();

  const handleConfirm = useCallback(() => {
    Modal.confirm({
      title: 'Envio do XML para Bosch',
      content:
        'Deseja enviar o arquivo XML da nota fiscal de compra para a Bosch?',
      okText: 'Enviar',
      onOk: () => {
        if (purchase?.id) {
          return sendBoschXml.mutateAsync(purchase.id);
        }
      },
    });
  }, [purchase?.id]);

  const popoverContent = useMemo(() => {
    if (
      !purchase?.boschXmlSendStatus ||
      purchase?.boschXmlSendStatus === PurchaseBoschXmlStatus.PENDING
    )
      return null;

    return (
      <div>
        <Group gap={10}>
          <strong style={{ fontSize: '1.15' }}>Status:</strong>
          <StatusTag
            label={PurchaseBoschXmlStatusLabel[purchase.boschXmlSendStatus]}
            color={PurchaseBoschXmlStatusColor[purchase.boschXmlSendStatus]}
          />
        </Group>

        {purchase.boschXmlSendStatus === PurchaseBoschXmlStatus.REJECTED && (
          <p>O envio do XML foi rejeitado pela BOSCH</p>
        )}

        {purchase.boschXmlSendStatus === PurchaseBoschXmlStatus.DELIVERED && (
          <Group gap={10}>
            <strong style={{ fontSize: '1.15' }}>Envio:</strong>
            {moment(purchase.boschXmlSendDate).format('DD/MM/YYYY HH:mm:ss')}
          </Group>
        )}
      </div>
    );
  }, [purchase]);

  if (
    matrixDetail.data?.partnerOrigin !==
      MatrixPartnerOrigin.BOSCH_CAR_SERVICE ||
    !purchase?.xmlPath
  )
    return null;

  return (
    <Col span={24} md={12} lg={6} xxl={3}>
      <Popover trigger={['hover', 'click']} content={popoverContent}>
        <Button
          onClick={handleConfirm}
          disabled={!purchase?.id || disabled || isDelivered || isRejected}
          loading={sendBoschXml.isLoading}
        >
          {isDelivered
            ? 'XML enviado à Bosch'
            : isRejected
            ? 'XML Rejeitado'
            : 'Enviar XML à Bosch'}
        </Button>
      </Popover>
    </Col>
  );
}
